import { styled } from "@linaria/react";
import type { h } from "preact";
import { useMemo } from "preact/hooks";
import type { CSSProperties as ReactCSSProperties } from "react";

type CompatibleStyle = h.JSX.CSSProperties & ReactCSSProperties;

import {
  textActionL,
  textActionM,
  textActionS,
  textBoldL,
  textBoldM,
  textBoldS,
  textBoldXS,
  textRegularL,
  textRegularM,
  textRegularS,
  textRegularXS,
  textXL,
} from "@/components/AppStyle";

interface ITextProps {
  children: JSX.Element | JSX.Element[] | string | number;
  size: "XL" | "L" | "M" | "S" | "XS";
  isBold?: boolean;
  isAction?: boolean;
  color?: string;
  className?: string;
  style?: CompatibleStyle;
}

const Text = (
  { children, size, isBold, isAction, color, style, ...props }: ITextProps = {
    children: "",
    size: "M",
    isBold: false,
    isAction: false,
    color: "",
  },
): JSX.Element | null => {
  const StyledSpan = useMemo((): ReturnType<typeof styled.span> | null => {
    if (isAction) {
      switch (size) {
        case "L":
        case "XL":
          return styled.span`
            ${textActionL}
          `;
        case "M":
          return styled.span`
            ${textActionM}
          `;
        case "S":
        case "XS":
          return styled.span`
            ${textActionS}
          `;
      }
    } else {
      if (isBold) {
        switch (size) {
          case "XL":
            return styled.span`
              ${textXL}
            `;
          case "L":
            return styled.span`
              ${textBoldL}
            `;
          case "M":
            return styled.span`
              ${textBoldM}
            `;
          case "S":
            return styled.span`
              ${textBoldS}
            `;
          case "XS":
            return styled.span`
              ${textBoldXS}
            `;
        }
      } else {
        switch (size) {
          case "L":
            return styled.span`
              ${textRegularL}
            `;
          case "M":
            return styled.span`
              ${textRegularM}
            `;
          case "S":
            return styled.span`
              ${textRegularS}
            `;
          case "XS":
            return styled.span`
              ${textRegularXS}
            `;
        }
      }
    }

    // No option fits
    return null;
  }, [children, size, isBold, isAction, color]);

  if (!StyledSpan) {
    return null;
  }

  const mergedStyle: CompatibleStyle | undefined =
    color || style
      ? {
          ...((style as CompatibleStyle) || {}),
          ...(color ? { color } : {}),
        }
      : undefined;

  return (
    <StyledSpan {...(props as Record<string, unknown>)} style={mergedStyle}>
      {children}
    </StyledSpan>
  );
};

export default Text;
